import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { Upload, X, ChevronLeft, ChevronRight, Sparkles, AlertCircle } from "lucide-react";
import { Progress } from "../components/ui/progress";

export default function StudioPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1); // 1: Upload, 2: Select Garment, 3: Generating
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [garments, setGarments] = useState([]);
  const [selectedGarment, setSelectedGarment] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    fetchGarments();
    refreshUser();
  }, []);

  const fetchGarments = async () => {
    try {
      const response = await axios.get(`${API}/garments`);
      setGarments(response.data);
    } catch (error) {
      toast.error("Failed to load garments");
    }
  };

  const canGenerate = user?.is_subscribed 
    ? (user?.generations_used || 0) < 50 
    : (user?.free_tries_used || 0) < 3;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target?.result);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/try-on/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadId(response.data.upload_id);
      toast.success("Photo uploaded!");
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
      setUploadedImage(null);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!uploadId || !selectedGarment) {
      toast.error("Please upload a photo and select a garment");
      return;
    }

    if (!canGenerate) {
      toast.error("You've used all your generations. Please subscribe to continue.");
      navigate('/subscription');
      return;
    }

    setGenerating(true);
    setStep(3);

    try {
      const response = await axios.post(`${API}/try-on/generate`, {
        upload_id: uploadId,
        garment_id: selectedGarment.garment_id
      }, { withCredentials: true });

      toast.success("Try-on generated!");
      navigate(`/result/${response.data.result_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
      setStep(2);
    } finally {
      setGenerating(false);
    }
  };

  const resetUpload = () => {
    setUploadedImage(null);
    setUploadId(null);
    setSelectedGarment(null);
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="studio-page">
      <Navbar />
      
      <main className="pt-24 md:pt-28 pb-12">
        <div className="container-custom">
          {/* Header */}
          <div className="mb-8">
            <p className="caption text-[#FF1493] mb-2">Studio</p>
            <h1 className="heading-lg">Virtual Try-On</h1>
          </div>

          {/* Usage Warning */}
          {!canGenerate && (
            <div className="bg-[#FF1493]/10 border border-[#FF1493]/30 p-4 mb-8 flex items-center gap-3" data-testid="usage-warning">
              <AlertCircle className="w-5 h-5 text-[#FF1493] flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Generation limit reached</p>
                <p className="text-gray-400 text-sm">
                  {user?.is_subscribed 
                    ? "You've used all 50 generations this month. Wait for renewal or contact support."
                    : "Your free trials have been used. Subscribe to continue creating."}
                </p>
              </div>
              {!user?.is_subscribed && (
                <button 
                  onClick={() => navigate('/subscription')}
                  className="btn-primary ml-auto flex-shrink-0"
                >
                  Subscribe
                </button>
              )}
            </div>
          )}

          {/* Steps Progress */}
          <div className="flex items-center gap-4 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center font-mono text-sm ${
                  step >= s ? 'bg-[#FF1493] text-white' : 'bg-white/10 text-gray-500'
                }`}>
                  {s}
                </div>
                <span className={`text-sm ${step >= s ? 'text-white' : 'text-gray-500'}`}>
                  {s === 1 ? 'Upload' : s === 2 ? 'Select' : 'Generate'}
                </span>
                {s < 3 && <div className={`w-12 h-px ${step > s ? 'bg-[#FF1493]' : 'bg-white/10'}`}></div>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left - Upload Area */}
            <div className="lg:col-span-1">
              <div className="bg-[#141414] border border-white/10 p-6" data-testid="upload-section">
                <h3 className="font-heading text-lg mb-4 text-white">Your Photo</h3>
                
                {!uploadedImage ? (
                  <div 
                    className="upload-zone aspect-[3/4] flex flex-col items-center justify-center"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-zone"
                  >
                    {uploadLoading ? (
                      <div className="w-10 h-10 spinner"></div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-gray-500 mb-4" />
                        <p className="text-gray-400 text-sm mb-2">Click to upload</p>
                        <p className="text-gray-600 text-xs">PNG, JPG up to 10MB</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="relative aspect-[3/4]">
                    <img
                      src={uploadedImage}
                      alt="Uploaded"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={resetUpload}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/50 text-white flex items-center justify-center hover:bg-[#FF1493]"
                      data-testid="remove-photo-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>
            </div>

            {/* Center - Garment Selection */}
            <div className="lg:col-span-2">
              <div className="bg-[#141414] border border-white/10 p-6" data-testid="garment-section">
                <h3 className="font-heading text-lg mb-4 text-white">Select Design</h3>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {garments.map((garment) => (
                    <div
                      key={garment.garment_id}
                      onClick={() => setSelectedGarment(garment)}
                      className={`cursor-pointer group ${
                        selectedGarment?.garment_id === garment.garment_id 
                          ? 'ring-2 ring-[#FF1493]' 
                          : ''
                      }`}
                      data-testid={`garment-${garment.garment_id}`}
                    >
                      <div className="aspect-[3/4] overflow-hidden bg-[#1a1a1a]">
                        <img
                          src={garment.thumbnail_url}
                          alt={garment.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <p className="font-medium text-sm truncate text-white">{garment.name}</p>
                        <p className="text-xs text-[#FF1493]">{garment.category}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Generate Button */}
                <div className="mt-8 pt-6 border-t border-white/10">
                  <button
                    onClick={handleGenerate}
                    disabled={!uploadId || !selectedGarment || generating || !canGenerate}
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="generate-btn"
                  >
                    {generating ? (
                      <>
                        <div className="w-5 h-5 spinner border-white/30 border-t-white"></div>
                        Generating Magic...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generate Try-On
                      </>
                    )}
                  </button>
                  
                  {generating && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-400 text-center mb-2">
                        AI is creating your virtual try-on...
                      </p>
                      <Progress value={66} className="h-1" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
